import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useOrdenCompraStore } from '../store/ordenCompraStore';
import { OrdenCompraService } from '../services/ordenCompraService';

export const OrdenCompraFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nueva';
  const { selectedItem, loading, fetchById, save } = useOrdenCompraStore();

  const [formData, setFormData] = useState<any>({
    idOrdenCompra: 0, numeroOrden: '', idProveedor: null, observaciones: '',
    condicionesPago: '', direccionEntrega: '', contacto: '',
    estado: 'PEN', detalles: [],
  });

  useEffect(() => { if (isEdit && id) fetchById(parseInt(id)); }, [id]);
  useEffect(() => {
    if (isEdit && selectedItem) setFormData({ ...selectedItem, detalles: selectedItem.detalles || [] });
  }, [selectedItem]);

  const handleChange = (field: string, value: any) =>
    setFormData((prev: any) => ({ ...prev, [field]: value }));

  const handleDetalleChange = (index: number, field: string, value: any) => {
    setFormData((prev: any) => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const addDetalle = () =>
    setFormData((prev: any) => ({
      ...prev, detalles: [...prev.detalles, { idArticulo: null, descripcion: '', unidadMedida: '', cantidad: 1, precioUnitario: 0, descuento: 0 }],
    }));

  const removeDetalle = (index: number) =>
    setFormData((prev: any) => ({ ...prev, detalles: prev.detalles.filter((_: any, i: number) => i !== index) }));

  const totales = useMemo(() => {
    let subTotal = 0;
    formData.detalles.forEach((d: any) => {
      subTotal += (d.cantidad || 0) * (d.precioUnitario || 0) - (d.descuento || 0);
    });
    const igv = subTotal * 0.18;
    return { subTotal, igv, total: subTotal + igv };
  }, [formData.detalles]);

  const handleSubmit = async () => {
    try { await save(formData); navigate('/compras/orden-compra'); } catch { /* handled */ }
  };

  const handleAprobar = async () => {
    if (!isEdit || !id) return;
    try { await OrdenCompraService.aprobar(parseInt(id), 0); navigate('/compras/orden-compra'); } catch { /* handled */ }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/compras/orden-compra')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">{isEdit ? 'Editar Orden de Compra' : 'Nueva Orden de Compra'}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos Generales</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Numero Orden" size="small" fullWidth value={formData.numeroOrden || ''} onChange={(e) => handleChange('numeroOrden', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="ID Proveedor" size="small" fullWidth type="number" value={formData.idProveedor || ''} onChange={(e) => handleChange('idProveedor', parseInt(e.target.value) || null)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Fecha Orden" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
              value={formData.fechaOrden?.substring(0, 10) || new Date().toISOString().substring(0, 10)}
              onChange={(e) => handleChange('fechaOrden', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Fecha Entrega" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }}
              value={formData.fechaEntrega?.substring(0, 10) || ''} onChange={(e) => handleChange('fechaEntrega', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Condiciones de Pago" size="small" fullWidth value={formData.condicionesPago || ''} onChange={(e) => handleChange('condicionesPago', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Direccion Entrega" size="small" fullWidth value={formData.direccionEntrega || ''} onChange={(e) => handleChange('direccionEntrega', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField label="Contacto" size="small" fullWidth value={formData.contacto || ''} onChange={(e) => handleChange('contacto', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField label="Observaciones" size="small" fullWidth multiline rows={2} value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Detalle</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Linea</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>Descripcion</TableCell>
                <TableCell width={100}>Cantidad</TableCell>
                <TableCell width={120}>P. Unitario</TableCell>
                <TableCell width={100}>Descuento</TableCell>
                <TableCell width={120}>SubTotal</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.detalles.map((det: any, idx: number) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell><TextField size="small" fullWidth value={det.descripcion || ''} onChange={(e) => handleDetalleChange(idx, 'descripcion', e.target.value)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.cantidad || 0} onChange={(e) => handleDetalleChange(idx, 'cantidad', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.precioUnitario || 0} onChange={(e) => handleDetalleChange(idx, 'precioUnitario', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.descuento || 0} onChange={(e) => handleDetalleChange(idx, 'descuento', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell align="right">S/ {((det.cantidad || 0) * (det.precioUnitario || 0) - (det.descuento || 0)).toFixed(2)}</TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {formData.detalles.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin lineas de detalle</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 250 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Sub Total:</Typography><Typography variant="body2">S/ {totales.subTotal.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">IGV (18%):</Typography><Typography variant="body2">S/ {totales.igv.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Total:</Typography><Typography variant="h6">S/ {totales.total.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        {isEdit && formData.estado === 'PEN' && (
          <Button variant="outlined" color="success" startIcon={<CheckCircleIcon />} onClick={handleAprobar}>Aprobar</Button>
        )}
        <Button variant="outlined" onClick={() => navigate('/compras/orden-compra')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </Box>
    </Box>
  );
};
